/**
 * WebSocket service for real-time communication with DiceCapture backend
 *
 * Handles connection management, message routing, and reconnection logic
 */

import { createScopedReporter } from './errorReporter';

const SERVICE_TAG = '[WEBSOCKET]';
const reporter = createScopedReporter('WEBSOCKET');

// Message types from backend
export interface RollRequestMessage {
  type: 'rollRequest';
  requestId: string;
  playerId: string;
  dice: Array<{ type: string; count: number }>;
  context: string;
  flavor?: string;
  /** Timestamp (ms) when the roll request was originally created. Present on pending roll delivery. */
  createdAt?: number;
  /** Proof requirement: image | video | either | none (TICKET-281c, default 'either') */
  proofMode?: 'image' | 'video' | 'either' | 'none';
}

export interface RollResultMessage {
  type: 'rollResult';
  requestId: string;
  playerId: string;
  results: Array<{
    type: string;
    value: number;
    wasEdited: boolean;
    confidence: number;
    originalValue?: number;
  }>;
  /** Base64-encoded image of the captured dice (optional, used for Discord embeds) */
  imageData?: string;
  /** Pre-signed S3 GET URL for the captured dice image (24h TTL, WebSocket path) */
  imageUrl?: string;
  /** Pre-signed S3 GET URL for the captured dice video (24h TTL, TICKET-281c) */
  videoUrl?: string;
}

/**
 * Roll cancellation message (TICKET-289).
 *
 * Phone clients SEND this when the player aborts an in-flight roll request via
 * the Cancel button on `RollPage`. The backend fans it out to every platform
 * connection in the same session so the requesting platform (Foundry, Discord,
 * standalone) can clean up its pending state and notify the GM.
 */
export interface RollCancelledMessage {
  type: 'rollCancelled';
  requestId: string;
  playerId: string;
  sessionCode: string;
  reason?: string;
}

export interface PlayerListMessage {
  type: 'playerList';
  players: Array<{ id: string; name: string; color: string; linkedPlatforms?: string[] }>;
}

export interface ConnectionAckMessage {
  type: 'connectionAck';
  sessionId: string;
  sessionCode?: string;
  sessionName?: string;
}

export interface ErrorMessage {
  type: 'error';
  message: string;
  code?: string;
}

export interface TrainingStatusMessage {
  type: 'trainingStatus';
  registrationId: string;
  status: 'started' | 'progress' | 'complete' | 'failed';
  /** 0-100 percentage */
  progress?: number;
  modelArn?: string;
  error?: string;
}

export type WebSocketMessage =
  | RollRequestMessage
  | RollResultMessage
  | RollCancelledMessage
  | PlayerListMessage
  | ConnectionAckMessage
  | ErrorMessage
  | TrainingStatusMessage;

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

interface WebSocketServiceConfig {
  onStatusChange?: (status: ConnectionStatus) => void;
  onRollRequest?: (message: RollRequestMessage) => void;
  onRollCancelled?: (message: RollCancelledMessage) => void;
  onPlayerList?: (message: PlayerListMessage) => void;
  onError?: (message: ErrorMessage) => void;
  onConnectionAck?: (message: ConnectionAckMessage) => void;
  onTrainingStatus?: (message: TrainingStatusMessage) => void;
}

export class WebSocketClient {
  private socket: WebSocket | null = null;
  private config: WebSocketServiceConfig = {};
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private heartbeatInterval: number | null = null;
  private connectionTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private connectionParams: {
    serverUrl: string;
    sessionId: string;
    role: 'platform' | 'phone' | 'host' | 'foundry';
    playerId?: string;
    playerName?: string;
    sessionName?: string;
  } | null = null;
  private endpointId: string;

  constructor(endpointId: string) {
    this.endpointId = endpointId;
    console.info(`${SERVICE_TAG} Created WebSocket client`, { endpointId });
  }

  /**
   * Configure event handlers. Merges incoming handlers into the existing
   * config so that a second call does not silently discard previously
   * registered handlers.
   */
  configure(config: WebSocketServiceConfig): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Connect to WebSocket server
   */
  async connect(params: {
    serverUrl: string;
    sessionId: string;
    role: 'platform' | 'phone' | 'host' | 'foundry';
    playerId?: string;
    playerName?: string;
    sessionName?: string;
  }): Promise<void> {
    if (this.socket?.readyState === WebSocket.OPEN) {
      console.warn(`${SERVICE_TAG} Already connected`);
      return;
    }

    this.connectionParams = params;
    this.updateStatus('connecting');

    return new Promise((resolve, reject) => {
      try {
        // Build WebSocket URL with query parameters
        const url = new URL(params.serverUrl);
        url.searchParams.set('sessionId', params.sessionId);
        url.searchParams.set('role', params.role);
        if (params.playerId) {
          url.searchParams.set('playerId', params.playerId);
        }
        if (params.playerName) {
          url.searchParams.set('playerName', params.playerName);
        }

        console.info(`${SERVICE_TAG} Connecting to`, { url: url.toString() });

        this.socket = new WebSocket(url.toString());

        this.socket.onopen = () => {
          console.info(`${SERVICE_TAG} Connected`);
          this.clearConnectionTimeout();
          this.reconnectAttempts = 0;
          this.updateStatus('connected');
          this.startHeartbeat();
          this.sendJoinSession();
          resolve();
        };

        this.socket.onclose = (event) => {
          console.info(`${SERVICE_TAG} Disconnected`, {
            code: event.code,
            reason: event.reason
          });
          this.clearConnectionTimeout();
          this.stopHeartbeat();
          this.updateStatus('disconnected');

          // Attempt reconnection if not intentional disconnect
          if (event.code !== 1000 && this.connectionParams) {
            this.attemptReconnect();
          }
        };

        this.socket.onerror = () => {
          this.clearConnectionTimeout();
          reporter.error('Connection failed', { showToast: false }); // SettingsPage handles display
          reject(new Error('WebSocket connection failed'));
        };

        this.socket.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        // Set connection timeout
        this.connectionTimeoutId = setTimeout(() => {
          this.connectionTimeoutId = null;
          if (this.socket?.readyState !== WebSocket.OPEN) {
            this.socket?.close();
            reject(new Error('Connection timeout'));
          }
        }, 10000);
      } catch (error) {
        reporter.error('Failed to create connection', {
          originalError: error instanceof Error ? error : undefined,
          showToast: false // SettingsPage handles display
        });
        this.updateStatus('disconnected');
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    console.info(`${SERVICE_TAG} Disconnecting`);
    this.connectionParams = null;
    this.clearConnectionTimeout();
    this.clearReconnectTimeout();
    this.stopHeartbeat();

    if (this.socket) {
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }

    this.updateStatus('disconnected');
  }

  /**
   * Send join session message
   *
   * @param sessionCode - The session code to join
   * @param playerId - The player's unique ID
   * @param playerName - Optional display name for the player
   * @param sessionName - Optional human-readable session name (sent by starters)
   */
  joinSession(sessionCode: string, playerId?: string, playerName?: string, sessionName?: string): void {
    this.sendMessage({
      action: 'joinSession',
      sessionCode,
      playerId,
      playerName,
      ...(sessionName ? { sessionName } : {})
    });
  }

  /**
   * Send roll result message
   *
   * @returns `true` if the message was sent, `false` if the socket was not open
   */
  sendRollResult(result: RollResultMessage): boolean {
    return this.sendMessage({
      action: 'forwardMessage',
      ...result
    });
  }

  /**
   * Send a roll cancellation notice to the backend (TICKET-289).
   *
   * Used by phone clients when the player aborts a pending roll request.
   * The backend fans the `rollCancelled` event out to all platform-role
   * connections in the same session so the requesting platform can clean
   * up its pending state.
   *
   * @param requestId - The ID of the pending roll request being cancelled
   * @param playerId - The ID of the player issuing the cancellation
   * @param sessionCode - The session code the cancellation belongs to
   * @param reason - Optional human-readable reason (currently unused server-side)
   * @returns `true` if the message was sent, `false` if the socket was not open
   */
  sendRollCancellation(requestId: string, playerId: string, sessionCode: string, reason?: string): boolean {
    return this.sendMessage({
      action: 'forwardMessage',
      type: 'rollCancelled',
      requestId,
      playerId,
      sessionCode,
      ...(reason ? { reason } : {})
    });
  }

  /**
   * Send rename player request to change the player's display name mid-session.
   * Only valid for phone clients. The backend resolves name uniqueness
   * and broadcasts the updated player list to all session connections.
   *
   * @param newName - The new display name requested by the player
   */
  sendRename(newName: string): void {
    this.sendMessage({
      action: 'forwardMessage',
      type: 'renamePlayer',
      newName
    });
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  /**
   * Get current connection status
   */
  getStatus(): ConnectionStatus {
    if (!this.socket) return 'disconnected';
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      default:
        return 'disconnected';
    }
  }

  /**
   * Get endpoint ID this client is associated with
   */
  getEndpointId(): string {
    return this.endpointId;
  }

  /**
   * Whether this client has stored connection params for reconnection
   */
  hasConnectionParams(): boolean {
    return this.connectionParams !== null;
  }

  /**
   * Reset reconnect state and immediately attempt a fresh connection.
   *
   * Used by connectionManager.reconcileConnections() on phone wake to
   * re-establish connections that died while the screen was locked.
   */
  resetAndReconnect(): void {
    console.info(`${SERVICE_TAG} Resetting and reconnecting`, { endpointId: this.endpointId });

    // Clean up existing socket without triggering onclose → attemptReconnect
    this.clearReconnectTimeout();
    this.stopHeartbeat();
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.close();
      this.socket = null;
    }

    // Reset counter and reconnect with stored params
    this.reconnectAttempts = 0;
    if (this.connectionParams) {
      this.updateStatus('connecting');
      this.connect(this.connectionParams).catch((error) => {
        reporter.error('Auto-reconnect on wake failed', {
          originalError: error instanceof Error ? error : undefined,
          context: { endpointId: this.endpointId },
          showToast: false
        });
      });
    }
  }

  private sendMessage(message: Record<string, unknown>): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      reporter.error('Cannot send message - not connected', { showToast: false });
      return false;
    }

    const payload = JSON.stringify(message);
    console.info(`${SERVICE_TAG} Sending message`, { action: message.action });
    this.socket.send(payload);
    return true;
  }

  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data) as WebSocketMessage;
      console.info(`${SERVICE_TAG} Received message`, { type: message.type });

      switch (message.type) {
        case 'rollRequest':
          this.config.onRollRequest?.(message);
          break;
        case 'rollCancelled':
          this.config.onRollCancelled?.(message);
          break;
        case 'playerList':
          this.config.onPlayerList?.(message);
          break;
        case 'connectionAck':
          this.config.onConnectionAck?.(message);
          break;
        case 'error':
          this.config.onError?.(message);
          break;
        case 'trainingStatus':
          this.config.onTrainingStatus?.(message);
          break;
        default:
          reporter.warning('Unknown message type received', {
            context: { type: (message as { type?: string }).type },
            showToast: false
          });
      }
    } catch (error) {
      reporter.error('Failed to parse message', {
        originalError: error instanceof Error ? error : undefined,
        showToast: false
      });
    }
  }

  private updateStatus(status: ConnectionStatus): void {
    this.config.onStatusChange?.(status);
  }

  private startHeartbeat(): void {
    // Send heartbeat every 30 seconds to keep connection alive
    this.heartbeatInterval = window.setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.sendMessage({ action: 'heartbeat' });
      }
    }, 30000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeoutId !== null) {
      clearTimeout(this.connectionTimeoutId);
      this.connectionTimeoutId = null;
    }
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  /**
   * Send joinSession message using stored connection params.
   * Called automatically after connect and on reconnect.
   */
  private sendJoinSession(): void {
    if (!this.connectionParams) return;
    const { sessionId, playerId, playerName, sessionName } = this.connectionParams;
    console.info(`${SERVICE_TAG} Joining session`, { sessionCode: sessionId });
    this.joinSession(sessionId, playerId, playerName, sessionName);
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      reporter.warning('Max reconnect attempts reached', {
        context: { attempts: this.maxReconnectAttempts }
      });
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.info(`${SERVICE_TAG} Attempting reconnect`, {
      attempt: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
      delayMs: delay
    });

    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null;
      if (this.connectionParams) {
        this.connect(this.connectionParams).catch((error) => {
          reporter.error('Reconnect failed', {
            originalError: error instanceof Error ? error : undefined,
            context: { attempt: this.reconnectAttempts },
            showToast: false
          });
        });
      }
    }, delay);
  }
}

/**
 * Factory function to create a new WebSocket client for an endpoint
 */
export function createWebSocketClient(endpointId: string): WebSocketClient {
  return new WebSocketClient(endpointId);
}

/**
 * Legacy singleton export for backward compatibility
 * @deprecated Use createWebSocketClient() instead for multi-connection support
 */
export const websocketService = new WebSocketClient('legacy-singleton');
